import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
} from '../../ui';
import { WORKSTREAM_STAGES } from '../../features/workstreams/workstreamConfig';
import {
  blankWorkstreamDraft,
  draftFromWorkstream,
  listToInput,
  listToLines,
  parseList,
  validateWorkstreamDraft,
} from '../../features/workstreams/workstreamModel';
import type { StructuredWorkstreamFields } from '../../features/workstreams/aiStructure';
import type {
  Workstream,
  WorkstreamDraft,
} from '../../features/workstreams/types';
import type { GoalSummary } from '../../features/goals/types';
import { PeopleInput } from '../common/PeopleInput';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';

export interface WorkstreamModalProps {
  open: boolean;
  onClose: () => void;
  /** When set, the modal edits this workstream; otherwise it creates a new one. */
  workstream?: Workstream | null;
  /** Existing goals — used to populate the linked-goal picker. */
  goals: GoalSummary[];
  /** Default owner/POC alias for a new workstream (the current user). */
  defaultEngineer: string;
  onCreate: (draft: WorkstreamDraft) => Promise<Workstream>;
  onSave?: (draft: WorkstreamDraft) => Promise<Workstream>;
  /** AI-STRUCTURE: turns free-form notes into partial draft fields. */
  onStructure: (notes: string) => Promise<StructuredWorkstreamFields>;
}

function stageOptions(): SelectOption[] {
  return WORKSTREAM_STAGES.map((s) => ({ value: s.value, label: s.label }));
}

/**
 * Create / edit form for a Workstream. Follows the team-knowledge pipeline:
 * a human can paste free-form notes (the manual stand-in for AUTO-COLLECT, with
 * a clearly-marked extension point for Jira/repo/deploy ingestion) and use the
 * real Bedrock converse op to STRUCTURE them into the fields below, then VERIFY
 * / edit before saving. Multi-value fields accept comma or newline separated
 * values. Reads/writes only a {@link WorkstreamDraft}; persistence is the
 * caller's.
 */
export function WorkstreamModal({
  open,
  onClose,
  workstream,
  goals,
  defaultEngineer,
  onCreate,
  onSave,
  onStructure,
}: WorkstreamModalProps) {
  const editing = !!workstream;
  const { people } = usePeopleDirectory();
  const [draft, setDraft] = useState<WorkstreamDraft>(
    blankWorkstreamDraft(defaultEngineer),
  );
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Raw text mirrors for the multi-value inputs.
  const [systemsText, setSystemsText] = useState('');
  const [impactText, setImpactText] = useState('');
  const [storiesText, setStoriesText] = useState('');
  const [dependenciesText, setDependenciesText] = useState('');

  // AI-STRUCTURE assist state.
  const [notes, setNotes] = useState('');
  const [structuring, setStructuring] = useState(false);
  const [structureError, setStructureError] = useState<string | null>(null);
  const [structureInfo, setStructureInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const base = workstream
      ? draftFromWorkstream(workstream)
      : blankWorkstreamDraft(defaultEngineer);
    setDraft(base);
    setProblems([]);
    setSystemsText(listToInput(base.systemsAffected));
    setImpactText(listToLines(base.expectedImpact));
    setStoriesText(listToInput(base.sprintStoryIds));
    setDependenciesText(listToLines(base.dependencies));
    setNotes('');
    setStructureError(null);
    setStructureInfo(null);
  }, [open, workstream, defaultEngineer]);

  const goalOptions: SelectOption[] = goals.map((g) => ({
    value: g.id,
    label: g.title || g.id,
  }));

  const set = <K extends keyof WorkstreamDraft>(
    key: K,
    value: WorkstreamDraft[K],
  ) => setDraft((d) => ({ ...d, [key]: value }));

  const buildDraft = (): WorkstreamDraft => ({
    ...draft,
    engineer: draft.engineer.trim(),
    title: draft.title.trim(),
    poc: draft.poc.trim(),
    systemsAffected: parseList(systemsText),
    expectedImpact: parseList(impactText),
    sprintStoryIds: parseList(storiesText),
    dependencies: parseList(dependenciesText),
    goalId: draft.goalId || undefined,
  });

  const runStructure = async () => {
    if (!notes.trim()) return;
    setStructuring(true);
    setStructureError(null);
    setStructureInfo(null);
    try {
      const fields = await onStructure(notes);
      const keys = Object.keys(fields);
      if (keys.length === 0) {
        setStructureInfo('No structured fields were extracted. Fill the form in manually.');
        return;
      }
      // Merge only the fields the model produced; human verifies/edits after.
      if (fields.title !== undefined) set('title', fields.title);
      if (fields.why !== undefined) set('why', fields.why);
      if (fields.whatChanging !== undefined) set('whatChanging', fields.whatChanging);
      if (fields.technicalApproach !== undefined)
        set('technicalApproach', fields.technicalApproach);
      if (fields.currentStage !== undefined) set('currentStage', fields.currentStage);
      if (fields.systemsAffected !== undefined)
        setSystemsText(listToInput(fields.systemsAffected));
      if (fields.expectedImpact !== undefined)
        setImpactText(listToLines(fields.expectedImpact));
      if (fields.dependencies !== undefined)
        setDependenciesText(listToLines(fields.dependencies));
      setStructureInfo('Draft populated from notes — review and edit before saving.');
    } catch (e) {
      setStructureError(
        e instanceof Error ? e.message : 'AI structuring failed. Fill the form in manually.',
      );
    } finally {
      setStructuring(false);
    }
  };

  const submit = async () => {
    const next = buildDraft();
    const found = validateWorkstreamDraft(next);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setSaving(true);
    try {
      if (editing && onSave) await onSave(next);
      else await onCreate(next);
      onClose();
    } catch (e) {
      setProblems([e instanceof Error ? e.message : 'Failed to save workstream.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      size="xl"
      title={editing ? 'Edit workstream' : 'New workstream'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={submit}>
            {editing ? 'Save changes' : 'Create workstream'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {problems.length > 0 && (
          <ul className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        )}

        {/* AI-STRUCTURE assist */}
        <div className="rounded-xl border border-accent/30 bg-accent-soft/20 p-3">
          <div className="mb-1 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent-fg" aria-hidden="true" />
            <p className="text-sm font-medium text-zinc-100">
              Structure from notes
            </p>
          </div>
          <p className="mb-2 text-xs text-zinc-500">
            Paste free-form notes about your work and let AI structure them into
            the fields below — then review before saving. Auto-collection from
            Jira / repos / deploys is not available yet; this manual paste is the
            stand-in for that step.
          </p>
          <Textarea
            rows={3}
            placeholder="e.g. Migrating checkout to the new pricing service to cut latency; touching CheckoutService and PricingClient; currently building; blocked on the pricing team's API…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="mt-2 flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Sparkles className="h-4 w-4" />}
              loading={structuring}
              disabled={!notes.trim()}
              onClick={runStructure}
            >
              Structure with AI
            </Button>
            {structureInfo && (
              <span className="text-xs text-emerald-300">{structureInfo}</span>
            )}
            {structureError && (
              <span className="text-xs text-red-300">{structureError}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Title"
            placeholder="e.g. Migrate checkout to the new pricing service"
            value={draft.title}
            onChange={(e) => set('title', e.target.value)}
          />
          <PeopleInput
            label="Engineer (alias)"
            value={draft.engineer}
            people={people}
            onChange={(alias) => set('engineer', alias)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Select
            label="Current stage"
            value={draft.currentStage}
            options={stageOptions()}
            onChange={(e) => set('currentStage', e.target.value)}
          />
          <PeopleInput
            label="Point of contact (alias)"
            value={draft.poc}
            people={people}
            onChange={(alias) => set('poc', alias)}
          />
          <Select
            label="Linked goal"
            placeholder="None"
            value={draft.goalId ?? ''}
            options={goalOptions}
            onChange={(e) => set('goalId', e.target.value || undefined)}
          />
        </div>

        <Textarea
          label="Why (motivation / impact)"
          rows={2}
          value={draft.why}
          onChange={(e) => set('why', e.target.value)}
        />
        <Textarea
          label="What is changing"
          rows={2}
          value={draft.whatChanging}
          onChange={(e) => set('whatChanging', e.target.value)}
        />
        <Textarea
          label="Technical approach"
          rows={2}
          value={draft.technicalApproach}
          onChange={(e) => set('technicalApproach', e.target.value)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Systems affected (comma separated)"
            value={systemsText}
            onChange={(e) => setSystemsText(e.target.value)}
          />
          <Input
            label="Sprint story ids — Task ids (comma separated)"
            hint="Engineering work traced to this workstream."
            value={storiesText}
            onChange={(e) => setStoriesText(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Textarea
            label="Expected impact (one per line)"
            rows={3}
            value={impactText}
            onChange={(e) => setImpactText(e.target.value)}
          />
          <Textarea
            label="Dependencies / blocked on (one per line)"
            rows={3}
            value={dependenciesText}
            onChange={(e) => setDependenciesText(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

export default WorkstreamModal;
